import React, { useEffect, useRef, useState } from 'react';

import PriorityLinks, { CreateListingMenuLink, UserManagementLink } from './PriorityLinks';
import LinksMenu from './LinksMenu';

import {
  BellOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  RightOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { Button, Tooltip, Dropdown, Modal, Space, Typography, message } from 'antd';
const { Text, Title } = Typography;
import css from './CustomLinksMenu.module.css';
import { NamedLink } from '../../../../../components';
import { createResourceLocatorString } from '../../../../../util/routes';
import { manualTransactions } from '../../../../../util/api';
import classNames from 'classnames';
import { userTypeOf } from '../../../../../util/userType';
import { employerAnswerNoticeOf, employerAnswerNoticeText } from '../../../../../util/employerAnswerNotice';
import NoticeContent, { unseenNotices } from '../../NoticeContent';

const NotificationButton = ({ currentUserNotificationCount }) => {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Button 
        color="default" 
        variant="text" 
        size="large" 
        shape="circle" 
        icon={<MailOutlined />} 
      />
      {currentUserNotificationCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '8px',
            height: '8px',
            backgroundColor: 'red',
            borderRadius: '50%',
            border: '1px solid white'
          }}
        />
      )}
    </div>
  );
};

// Moved items generation inside component to access dynamic data

// Some requests report a refusal by resolving to false, others by answering success: false.
const isAnswered = result => !!result && result.success !== false;

const draftId = '00000000-0000-0000-0000-000000000000';
const createListingLinkConfig = intl => ({
  group: 'primary',
  text: intl.formatMessage({ id: 'TopbarDesktop.createListing' }),
  type: 'internal',
  route: {
    name: 'EditListingPage',
    params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
  },
  highlight: true,
});

/**
 * Group links to 2 groups:
 * - priorityLinks (Those primary links that fit into current width of the TopbarDesktop.)
 * - menuLinks (The rest of the links that are shown inside dropdown menu.)
 *
 * @param {*} links array of link configs in an order where primary group is shown first
 * @param {*} containerWidth width reserved for the CustomLinksMenu component
 * @param {*} menuMoreWidth width that the "More" label takes
 * @returns Object containing arrays: { priorityLinks, menuLinks }
 */
const groupMeasuredLinks = (links, containerWidth, menuMoreWidth) => {
  const isMeasured = !!links?.[0]?.width && menuMoreWidth > 0;
  const hasNoPrimaryLinks = !links.find(l => l.group === 'primary');
  // - We can't calculate groups, if the width of the rendered links are not measured
  // - We don't need to calculate groups, if links don't contain any 'primary' links
  if (!isMeasured || hasNoPrimaryLinks) {
    return { priorityLinks: [], menuLinks: links };
  }

  const groupedLinks = links.reduce(
    (pickedLinks, link, i) => {
      const isPrimary = link.group === 'primary';
      const isLast = i === links.length - 1;
      // Has menuLinks at this point of the iteration (seconary links are at the end of the array)
      const hasMenuLinks = pickedLinks.menuLinks?.length > 0;

      const hasSpace =
        isLast && !hasMenuLinks
          ? link.cumulatedWidth <= containerWidth
          : link.cumulatedWidth + menuMoreWidth <= containerWidth;

      return isPrimary && hasSpace
        ? {
            priorityLinks: [...pickedLinks.priorityLinks, link],
            menuLinks: pickedLinks.menuLinks,
          }
        : {
            priorityLinks: pickedLinks.priorityLinks,
            menuLinks: [...pickedLinks.menuLinks, link],
          };
    },
    { priorityLinks: [], menuLinks: [] }
  );
  return groupedLinks;
};

const calculateContainerWidth = (containerRefTarget, parentWidth) => {
  // Siblings include logo, search form, (inbox, profile menu || login signup)
  const siblingArray = containerRefTarget?.parentNode?.childNodes
    ? Array.from(containerRefTarget.parentNode.childNodes).filter(n => n !== containerRefTarget)
    : [];
  const siblingWidthsCombined = siblingArray.reduce((acc, node) => acc + node.offsetWidth, 0);

  // .root class of the TopbarDesktop has 24px padding on the right
  // Firefox doesn't support computedStyleMap()
  const parentStyleMap = containerRefTarget?.parentElement?.computedStyleMap
    ? containerRefTarget.parentElement.computedStyleMap()
    : null;
  const topbarPaddingRight = parentStyleMap?.get('padding-right')?.value;
  const padding = topbarPaddingRight != null ? topbarPaddingRight : 24;

  // We figure out available width from parent (TopbarDesktop/<nav>) and siblings
  const availableContainerWidth = parentWidth - siblingWidthsCombined - padding;
  return availableContainerWidth;
};

/**
 * Create CustomLinksMenu component that takes all the extra space from the TopbarDesktop and
 * renders primary links directly there if there's enough space - and secondary links are shown inside a "More" menu.
 * If the space is limited, primary links as well as secondary links are shown inside the "More" menu.
 *
 * Note: this component is inherently a bit fragile as it needs to deal with DOM directly. If you customize TopbarDesktop,
 * test the responsiveness thoroughly.
 *
 * props:
 * - customLinks: array of link configurations.
 * - hasClientSideContentReady: indicates if TopbarDesktop is ready to render
 * - currentPage: string that indicates if this is "LandingPage" or "SearchPage", etc.
 * - intl: React Intl instance
 *
 * @param {*} props contains currentPage, customLinks, intl, and hasClientSideContentReady
 * @returns component to be placed inside TopbarDesktop
 */
const CustomLinksMenu = ({
  currentPage,
  customLinks = [],
  hasClientSideContentReady,
  intl,
  currentUser,
  currentUserNotificationCount = 0,
  linkNotifications,
  onUpdateLinkedConsultant,
  onMarkLinkSeen,
  profileAccessRequests = [],
  integrityCompletionNotifications = [],
  onApproveAccessRequest,
  onRejectAccessRequest,
  onMarkIntegrityCompletionNotificationSeen,
  onMarkEmployerAnswerSeen,
  notices,
  onMarkNoticeSeen,
  history,
  routeConfiguration,
}) => {
  const containerRef = useRef(null);
  const observer = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [moreLabelWidth, setMoreLabelWidth] = useState(0);
  const [links, setLinks] = useState([createListingLinkConfig(intl), ...customLinks]);
  const [layoutData, setLayoutData] = useState({
    priorityLinks: links,
    menuLinks: links,
    containerWidth: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const isCandidate = userTypeOf(currentUser) === 'candidate';
  // The candidate's current employer answered and the candidate has not opened it yet.
  const employerAnswerNotice = isCandidate ? employerAnswerNoticeOf(currentUser) : null;
  const currentUserEmail = currentUser?.attributes?.email;
  const [myApprovedRequests, setMyApprovedRequests] = useState([]);
  // Jobs the agency applied this candidate to on their behalf (consent pending),
  // shown inside the accept-invitation modal so the candidate sees what they approve.
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [appliedJobsLoading, setAppliedJobsLoading] = useState(false);

  useEffect(() => {
    const data = selectedNotification?.data;
    const isRecruiterNotif = selectedNotification?.type === 'recruiter';
    const candidateUserId = data?.consultantId || currentUser?.id?.uuid;
    const agencyListingId = data?.recruiterListingId;

    if (!modalOpen || !isRecruiterNotif || !candidateUserId) {
      setAppliedJobs([]);
      return;
    }

    const payload = {
      type: 'fetch',
      consultantAdminId: candidateUserId,
      status: 'candidate consent pending',
      txType: 'apply',
      appliedByAgency: true,
      includeListingTitles: true,
    };
    if (agencyListingId) payload.agencyListingId = agencyListingId;

    let cancelled = false;
    setAppliedJobsLoading(true);
    manualTransactions(payload)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.transactions || [];
        setAppliedJobs(list);
      })
      .catch(() => {
        if (!cancelled) setAppliedJobs([]);
      })
      .finally(() => {
        if (!cancelled) setAppliedJobsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, selectedNotification]);

  useEffect(() => {
    if (!currentUserEmail) return;
    const fetchApproved = async () => {
      try {
        const response = await fetch(
          `/api/getProfileAccessRequests?params=${encodeURIComponent(
            JSON.stringify({ requesterEmail: currentUserEmail })
          )}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.approvedRequests)) {
            setMyApprovedRequests(data.approvedRequests);
          }
        }
      } catch (err) {
        // non-fatal
      }
    };
    fetchApproved();
  }, [currentUserEmail]);

  // Handle opening the modal with notification details
  const handleNotificationClick = async (notification) => {
    if (notification?.type === 'employerAnswer') {
      if (onMarkEmployerAnswerSeen) {
        await onMarkEmployerAnswerSeen(notification?.data?.answeredAt);
      }
      if (history && routeConfiguration) {
        history.push(createResourceLocatorString('ProfileSettingsPage', routeConfiguration, {}, {}));
      }
      return;
    }

    if (notification?.type === 'integrityCompletion') {
      const data = notification?.data || {};

      if (onMarkIntegrityCompletionNotificationSeen && data?.notificationId) {
        await onMarkIntegrityCompletionNotificationSeen({
          notificationId: data.notificationId,
          candidateListingId: data.candidateListingId,
          listingId: data.listingId,
        });
      }

      if (history && routeConfiguration) {
        const queryParams = data?.listingId ? { listingId: data.listingId } : {};
        history.push(
          createResourceLocatorString('JobPostsPage', routeConfiguration, { tab: 'applications' }, queryParams)
        );
      }

      setModalOpen(false);
      setSelectedNotification(null);
      return;
    }

    setSelectedNotification(notification);
    setModalOpen(true);
  };

  // Handle accept action
  const handleAccept = async () => {
    
    if (!selectedNotification || !onUpdateLinkedConsultant) return;

    const { data } = selectedNotification;

    let accepted = false;
    try {
      const result = await onUpdateLinkedConsultant({
        record: data,
        acceptRecord: true,
      });
      accepted = isAnswered(result);
    } catch (error) {
      console.error('Error accepting recruiter:', error);
    }

    if (!accepted) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.linkAcceptFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle reject action
  const handleReject = async () => {
    if (!selectedNotification || !onUpdateLinkedConsultant) return;

    const { data } = selectedNotification;

    let declined = false;
    try {
      // The decline is kept on the link and the agency is told; the link is not removed.
      const result = await onUpdateLinkedConsultant({
        record: data,
        declineRecord: true,
      });
      declined = isAnswered(result);
    } catch (error) {
      console.error('Error rejecting recruiter:', error);
    }

    if (!declined) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.linkDeclineFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Closing a notification: a consultant's answer is marked seen on its one link; nothing else
  // is saved.
  const handleMarkAsSeen = async () => {
    if (!selectedNotification) return;

    const { type, data } = selectedNotification;
    if (type === 'consultant' && data?._id && onMarkLinkSeen) {
      try {
        await onMarkLinkSeen({ _id: data._id });
      } catch (error) {
        console.error('Error marking notification as seen:', error);
      }
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle approve access request
  const handleApproveProfileAccess = async () => {
    if (!selectedNotification || !onApproveAccessRequest) return;

    const { type, data } = selectedNotification;
    if (type !== 'profileAccess') return;

    let approved = false;
    try {
      const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId;
      const profileListingSlug = data.profileListingSlug || currentUser?.attributes?.profile?.displayName?.toLowerCase().replace(/\s+/g, '-');
      const displayName = currentUser?.attributes?.profile?.displayName || `${currentUser?.attributes?.profile?.firstName} ${currentUser?.attributes?.profile?.lastName}`;

      const result = await onApproveAccessRequest({
        requestId: data._id,
        profileListingSlug,
        displayName,
      });
      approved = isAnswered(result);
    } catch (error) {
      console.error('Error approving access request:', error);
    }

    if (!approved) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.accessRequestFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle reject access request
  const handleRejectProfileAccess = async () => {
    if (!selectedNotification || !onRejectAccessRequest) return;

    const { type, data } = selectedNotification;
    if (type !== 'profileAccess') return;

    let rejected = false;
    try {
      const profileOwnerName = currentUser?.attributes?.profile?.displayName || `${currentUser?.attributes?.profile?.firstName} ${currentUser?.attributes?.profile?.lastName}`;
      const profileTitle = 'Profile';

      const result = await onRejectAccessRequest({
        requestId: data._id,
        profileOwnerName,
        profileTitle,
      });
      rejected = isAnswered(result);
    } catch (error) {
      console.error('Error rejecting access request:', error);
    }

    if (!rejected) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.accessRequestFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };
 
  const handleApprovedAccessClick = async (request) => {
  // mark as seen
  try {
    await fetch(
      `/api/getProfileAccessRequests?params=${encodeURIComponent(
        JSON.stringify({ markSeenId: request._id?.toString() })
      )}`
    );
    setMyApprovedRequests(prev =>
      prev.filter(r => r._id?.toString() !== request._id?.toString())
    );
  } catch (err) {}

  // Instead of reusing old token, request a fresh OTP on the existing token
  // by hitting requestShareOtp first to reset the cooldown
  if (request.shareToken && request.profileListingId && history && routeConfiguration) {
    const profileSlug =
      request.profileListingSlug ||
      (request.profileOwnerName || 'profile').toLowerCase().replace(/\s+/g, '-');

    // Pre-request the OTP now so by the time they land on the page it's ready
    try {
      await fetch('/api/requestShareOtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: request.shareToken }),
      });
    } catch (err) {}

    const path = createResourceLocatorString(
      'ListingPage',
      routeConfiguration,
      { id: request.profileListingId, slug: profileSlug },
      { share: request.shareToken }
    );
    history.push(path);
  }
};

  // Generate notification items based on user type
  const openNotice = notice => {
    if (onMarkNoticeSeen) onMarkNoticeSeen(notice.id);
    if (notice.link && history) history.push(notice.link);
  };

  const getNotificationItems = () => {
    let filteredItems = [];
    const approvedAccessItems = myApprovedRequests.map((request, index) => ({
      label: (
        <div
          className={css.notificationItem}
          onClick={() => handleApprovedAccessClick(request)}
        >
          <div className={css.notificationContent}>
            <UnlockOutlined style={{ color: '#0e9941', fontSize: 16, marginRight: 8 }} />
            <div className={css.notificationText}>
              <div>
                <Text strong>
                  {request.requestType === 'vault'
                    ? 'Credential vault access granted!'
                    : 'Profile access granted!'}
                </Text>
              </div>
              <div className={css.notificationSubText}>
                <Text type="secondary" className={css.notificationSubTextSecondary}>
                  {request.profileOwnerName || 'Profile owner'} approved your request. Click to view.
                </Text>
              </div>
            </div>
            <RightOutlined className={css.notificationArrow} />
          </div>
        </div>
      ),
      key: `approved-access-${index}`,
    }));

    // Add profile access requests for all users (not just candidates)
    const accessRequestItems = (profileAccessRequests || []).map((request, index) => ({
      label: (
        <div className={css.notificationItem} onClick={() => handleNotificationClick({ type: 'profileAccess', data: request })}>
          <div className={css.notificationContent}>
            <UserAddOutlined className={css.notificationIconAccess} />
            <div className={css.notificationText}>
              <div>
                <Text strong>{request.firstName} {request.lastName}</Text>
                <Text> from </Text>
                <Text strong>{request.companyName}</Text>
                <Text>{request.requestType === 'vault' ? ' requests credential vault access' : ' requests profile access'}</Text>              </div>
                <div className={css.notificationSubText}>
                <Text type="secondary" className={css.notificationSubTextSecondary}>
                  Click to view details
                </Text>
              </div>
            </div>
            <RightOutlined className={css.notificationArrow} />
          </div>
        </div>
      ),
      key: `access-request-${index}`,
    }));

    const integrityCompletionItems = (integrityCompletionNotifications || []).map((notification, index) => ({
      label: (
        <div
          className={css.notificationItem}
          onClick={() => handleNotificationClick({ type: 'integrityCompletion', data: notification })}
        >
          <div className={css.notificationContent}>
            <CheckCircleOutlined className={css.notificationIconConsultant} />
            <div className={css.notificationText}>
              <div>
                <Text strong>{notification?.candidateName || 'Candidate'}</Text>
                <Text> completed the integrity check</Text>
              </div>
              <div className={css.notificationSubText}>
                <Text type="secondary" className={css.notificationSubTextSecondary}>
                  Click to view application
                </Text>
              </div>
            </div>
            <RightOutlined className={css.notificationArrow} />
          </div>
        </div>
      ),
      key: `integrity-completion-${notification?.notificationId || index}`,
    }));

    if (isCandidate) {
      // For candidates: the link requests still waiting for their answer, newest first
      const pendingRequests = linkNotifications?.recruiterRequests || [];
      filteredItems = pendingRequests.map((recruiter, index) => ({
        label: (
          <div className={css.notificationItem} onClick={() => handleNotificationClick({ type: 'recruiter', data: recruiter })}>
            <div className={css.notificationContent}>
              <div className={css.notificationText}>
                <div>
                  <Text strong>{recruiter?.recruiterName || 'A recruiter'}</Text>
                  <Text> has added you as a consultant</Text>
                </div>
                {/* <div className={css.notificationSubText}>
                  <Text type="secondary" className={css.notificationSubTextSecondary}>
                    Click to view details
                  </Text>
                </div> */}
              </div>
              <RightOutlined className={css.notificationArrow} />
            </div>
          </div>
        ),
        key: `recruiter-${index}`,
      }));
      if (employerAnswerNotice) {
        filteredItems.unshift({
          label: (
            <div
              className={css.notificationItem}
              onClick={() => handleNotificationClick({ type: 'employerAnswer', data: employerAnswerNotice })}
            >
              <div className={css.notificationContent}>
                <CheckCircleOutlined className={css.notificationIconConsultant} />
                <div className={css.notificationText}>
                  <div>
                    <Text>{employerAnswerNoticeText(employerAnswerNotice)}</Text>
                  </div>
                  <div className={css.notificationSubText}>
                    <Text type="secondary" className={css.notificationSubTextSecondary}>
                      Click to view your profile settings
                    </Text>
                  </div>
                </div>
                <RightOutlined className={css.notificationArrow} />
              </div>
            </div>
          ),
          key: 'employer-answer',
        });
      }
    } else {
      // For organisations: consultants' answers not yet marked seen, newest first
      const allConsultants = linkNotifications?.consultantAnswers || [];

      filteredItems = allConsultants.map((consultant, index) => ({
        label: (
          <div className={css.notificationItem} onClick={() => handleNotificationClick({ type: 'consultant', data: consultant })}>
            <div className={css.notificationContent}>
              <CheckCircleOutlined className={css.notificationIconConsultant} />
              <div className={css.notificationText}>
                <div>
                  <Text strong>{consultant?.label || 'A consultant'}</Text>
                  <Text> has responded to your request</Text>
                </div>
                <div className={css.notificationSubText}>
                  <Text type="secondary" className={css.notificationSubTextSecondary}>
                    Click to view details
                  </Text>
                </div>
              </div>
              <RightOutlined className={css.notificationArrow} />
            </div>
          </div>
        ),
        key: `consultant-${index}`,
      }));
    }

    // Combine all notification items (access requests + other notifications)
    // The bell's notices: what was sent, alerts paused, the weekly digest. Opening one marks it seen.
    const noticeItems = unseenNotices(notices).map(notice => ({
      label: (
        <div className={css.notificationItem} onClick={() => openNotice(notice)}>
          <div className={css.notificationContent}>
            <CheckCircleOutlined className={css.notificationIconConsultant} />
            <div className={css.notificationText}>
              <NoticeContent notice={notice} />
            </div>
            <RightOutlined className={css.notificationArrow} />
          </div>
        </div>
      ),
      key: `notice-${notice.id}`,
    }));

    const allItems = [...noticeItems, ...accessRequestItems, ...approvedAccessItems, ...integrityCompletionItems, ...filteredItems];

    // If no items, show a default message
    if (allItems.length === 0) {
      return [{
        label: (
          <span style={{ color: '#999' }}>
            No new notifications
          </span>
        ),
        key: 'no-notifications',
      }];
    }

    return allItems;
  };

  const notificationItems = getNotificationItems();

  // Check if there are actual notifications (not just the "no notifications" message)
  const hasAccessRequestNotifications = (profileAccessRequests || []).length > 0;
  const hasIntegrityCompletionNotifications = (integrityCompletionNotifications || []).length > 0;
  const hasApprovedAccessNotifications = myApprovedRequests.length > 0;
  const hasUnseenLinks = !!linkNotifications?.hasUnseen;
  const hasNotifications = unseenNotices(notices).length > 0 || hasAccessRequestNotifications || hasApprovedAccessNotifications || hasUnseenLinks || (isCandidate
    ? !!employerAnswerNotice
    : hasIntegrityCompletionNotifications);


  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let animationFrameId = null;
    const body = document.body;
    const container = containerRef.current;

    if (hasClientSideContentReady && moreLabelWidth > 0) {
      // ResizeObserver sets layout data: grouped priority links and links that go to menuLinks dropdown
      observer.current = new ResizeObserver(entries => {
        const containerRefParentWidth = container?.parentNode?.offsetWidth;
        const bodyOffsetWidth = body.offsetWidth;

        for (const entry of entries) {
          // Body's width has changed (aka viewport has changed)
          const isBodyTheTarget = entry.target === body;
          // If the width of the TopbarDesktop (aka <nav>) changes, this assumes that the document.body has the correct width.
          const hasWidthOfTopbarDesktopChanged =
            !isBodyTheTarget && containerRefParentWidth !== bodyOffsetWidth;

          if (isBodyTheTarget || hasWidthOfTopbarDesktopChanged) {
            const target = container;
            const availableContainerWidth = calculateContainerWidth(target, bodyOffsetWidth);

            // The groupedLinks variable contains { priorityLinks, menuLinks }
            const groupedLinks = groupMeasuredLinks(links, availableContainerWidth, moreLabelWidth);
            // The setLayoutData call affects the UI. This pushes the painting to the next frame.
            animationFrameId = window.requestAnimationFrame(() => {
              if (container) {
                setLayoutData({ ...groupedLinks, containerWidth: availableContainerWidth });
              }
            });
            // After setLayoutData is called, don't process other entries
            break;
          }
        }
      });

      if (container) {
        // We need to observe both document body and the component's own container
        // When the window is squeezed, priority links prevent the container to shrink smaller.
        // At that point, we need to calculate the width from the width of the body.
        // It's also possible that some of the other elements get a repaint after window-level repaint (e.g. image loads)
        // In those cases, we just need to
        observer.current.observe(body);
        observer.current.observe(container);
      }
    }
    return () => {
      if (body) {
        observer.current?.unobserve(body);
      }
      if (container) {
        observer.current?.unobserve(container);
      }
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [containerRef, hasClientSideContentReady, moreLabelWidth, links]);

  const { priorityLinks, menuLinks, containerWidth } = layoutData;  

  // If there are no custom links, just render createListing link.
  if (customLinks?.length === 0) {
    return (
      <>
        {/* <UserManagementLink
          customLinksMenuClass={css.createListingLinkOnly}
          currentUser={currentUser}
        /> */}
        {currentUser && (
          <>
            <div className={css.topbarLink}>
              <Tooltip title="Help Center">
                <a href="https://help.iparley.com" target="_blank" rel="noopener noreferrer">
                  <Button color="default" variant="text" size='large' shape="circle" icon={<QuestionCircleOutlined style={{ color: 'var(--marketplaceColor)' }} />} />
                </a>
              </Tooltip>
            </div>
            <div className={css.topbarLink}>
              <Tooltip title="Notifications">
                <Dropdown menu={{ items: notificationItems }} trigger={['click']} placement="bottomRight" arrow>
                  <a onClick={e => e.preventDefault()}>
                    <div className={css.notificationBadgeContainer}>
                      <Button color="default" variant="text" size='large' shape="circle" icon={<BellOutlined />} />
                      {hasNotifications && <div className={css.notificationDot} />}
                    </div>
                  </a>
                </Dropdown>
              </Tooltip>
            </div>
            <div className={css.topbarLink}>
              <NamedLink name="CalendarPage" className={classNames(css.priorityLink, css.highlight)}>
                <Tooltip title="Calendar">
                  <Button color="default" variant="text" size='large' shape="circle" icon={<CalendarOutlined />} />
                </Tooltip>
              </NamedLink>
            </div>
            <div className={css.topbarLink}>
              <Tooltip title="Messages">
                <span>
                  <NamedLink name="InboxPage" params={{ tab:'sales' }} className={classNames(css.priorityLink, css.highlight)}>
                    <NotificationButton currentUserNotificationCount={currentUserNotificationCount} />
                  </NamedLink>
                </span>
              </Tooltip>
            </div>
          </>
        )}

      {/* {!isCandidate && (
        <CreateListingMenuLink customLinksMenuClass={css.createListingLinkOnly} />
      )} */}

        {/* Notification Modal */}
        <Modal
          title="Notification"
          open={modalOpen}
          onCancel={() => (selectedNotification?.type === 'recruiter' || selectedNotification?.type === 'consultant') ? handleMarkAsSeen() : setModalOpen(false)}
          footer={
            selectedNotification?.type === 'profileAccess' ? [
              <Button key="reject" danger onClick={handleRejectProfileAccess}>
                Reject
              </Button>,
              <Button key="approve" type="primary" onClick={handleApproveProfileAccess} style={{ backgroundColor: '#0e9941', borderColor: '#0e9941' }}>
                {selectedNotification?.data?.requestType === 'vault' ? 'Share Vault Access' : 'Approve'}
              </Button>,
            ] : selectedNotification?.type === 'recruiter' ? [
              <Button key="reject" danger onClick={handleReject}>
                Reject
              </Button>,
              <Button key="accept" type="primary" onClick={handleAccept}>
                Accept
              </Button>,
            ] : [
              <Button key="close" type="primary" onClick={handleMarkAsSeen}>
                Close
              </Button>,
            ]
          }
        >
          {selectedNotification && (
            <div>
              <Title level={5}>
                {selectedNotification.type === 'profileAccess'
                  ? `${selectedNotification?.data?.requestType === 'vault' ? 'Credential Vault Access Request' : 'Profile Access Request'} from ${selectedNotification?.data?.firstName} ${selectedNotification?.data?.lastName}`
                  : selectedNotification.type === 'recruiter'
                  ? `${selectedNotification?.data?.recruiterName || 'A recruiter'} has added you as a consultant`
                  : selectedNotification?.data?.status === 'left'
                  ? intl.formatMessage({ id: 'Topbar.linkAnswerLeft' }, { name: selectedNotification?.data?.label || 'A consultant' })
                  : `${selectedNotification?.data?.label || 'A consultant'} has ${selectedNotification?.data?.status === 'accepted' ? 'accepted' : 'declined' } your connection invitation`}
              </Title>

              {/* Profile Access Request Details */}
              {selectedNotification.type === 'profileAccess' && (
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ 
                      padding: '8px 12px', 
                      backgroundColor: selectedNotification.data.requestType === 'vault' ? '#f0f7ff' : '#f6ffed',
                      borderLeft: `3px solid ${selectedNotification.data.requestType === 'vault' ? '#0e9941' : '#52c41a'}`,
                      borderRadius: 4,
                      fontSize: 13,
                      color: '#555'
                    }}>
                      {selectedNotification.data.requestType === 'vault' 
                        ? 'This person is requesting access to view your credential vault.'
                        : 'This person is requesting access to view your full profile.'
                      }
                    </div>
                    <div><strong>Name:</strong> {selectedNotification.data.firstName} {selectedNotification.data.lastName}</div>
                    <div><strong>Email:</strong> {selectedNotification.data.email}</div>
                    <div><strong>Company:</strong> {selectedNotification.data.companyName}</div>
                    {selectedNotification.data.message && (
                      <div>
                        <strong>Message:</strong>
                        <div style={{ marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                          {selectedNotification.data.message}
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                      Requested on {new Date(selectedNotification.data.createdAt).toLocaleDateString()}
                    </div>
                  </Space>
                </div>
              )}

              {/* View Profile Link */}
              {selectedNotification?.type === 'recruiter' && selectedNotification.data.recruiterListingId && (
                <div className={css.profileLink}>
                  <NamedLink
                    name="ListingPage"
                    params={{
                      id: selectedNotification.data.recruiterListingId,
                      slug: selectedNotification.data.recruiterName?.toLowerCase().replace(/\s+/g, '-') || 'recruiter'
                    }}
                    className={css.profileLinkAnchor}
                  >
                    View {selectedNotification.data.recruiterName || 'Recruiter'}'s Profile
                  </NamedLink>
                </div>
              )}


              {selectedNotification?.type === 'recruiter' && (
                <div className={css.profileLink}>
                  <NamedLink name="MyNetworkPage" className={css.profileLinkAnchor}>
                    {intl.formatMessage({ id: 'Topbar.openMyNetwork' })}
                  </NamedLink>
                </div>
              )}

              {/* Info about where this will be displayed */}
              {selectedNotification.type === 'recruiter' ? (
                <div className={css.infoBox}>
                  <Text type="secondary" className={css.infoText}>
                    By accepting, <strong>{selectedNotification?.data?.recruiterName || 'this recruiter'}</strong> will be displayed as your linked Suppliers in your profile under the <strong>Current Employer</strong> section.
                  </Text>
                </div>
              ) : selectedNotification?.type == 'profileAccess' ?
                    // <div className={css.infoBox}>
                    //   <Text type="secondary" className={css.infoText}>
                    //     {selectedNotification?.data?.status === 'accepted' ?
                    //     `${selectedNotification?.data?.label} has accepted your connection invitation and will now be visible in your profile.`
                    //     :
                    //     `${selectedNotification?.data?.label} has declined your connection invitation and will not appear in your profile.`}
                    //   </Text>
                    // </div>
                    null
                  : null
              }

              {/* Jobs the agency applied this candidate to (consent gate) */}
              {selectedNotification.type === 'recruiter' && (appliedJobsLoading || appliedJobs.length > 0) && (
                <div style={{
                  border: '1px solid #ffe7ba',
                  backgroundColor: '#fffbe6',
                  padding: '12px',
                  borderRadius: '4px',
                  marginTop: '16px'
                }}>
                  <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
                    {appliedJobsLoading
                      ? 'Checking jobs applied on your behalf…'
                      : `${selectedNotification?.data?.recruiterName || 'This supplier'} has applied you to ${appliedJobs.length} job${appliedJobs.length === 1 ? '' : 's'} on your behalf:`}
                  </Text>
                  {!appliedJobsLoading && (
                    <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                      {appliedJobs.map(job => (
                        <li key={job.transactionId} style={{ fontSize: '13px', color: '#484848' }}>
                          {job.listingTitle || 'Job application'}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!appliedJobsLoading && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      By accepting, you approve every job this supplier has applied you to on your behalf.
                    </Text>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </>
    );
  }

  const styleMaybe = mounted ? { style: { width: `${containerWidth}px` } } : {};
  const isMeasured = !!links?.[0]?.width;
  const hasMenuLinks = menuLinks?.length > 0;
  const hasPriorityLinks = isMeasured && priorityLinks.length > 0;

  return (
    <div className={css.customLinksMenu} ref={containerRef} {...styleMaybe}>
      <PriorityLinks links={links} priorityLinks={priorityLinks} setLinks={setLinks} />
      {mounted && hasMenuLinks ? (
        <LinksMenu
          id="linksMenu"
          currentPage={currentPage}
          links={menuLinks}
          showMoreLabel={hasPriorityLinks}
          moreLabelWidth={moreLabelWidth}
          setMoreLabelWidth={setMoreLabelWidth}
          intl={intl}
        />
      ) : null}
    </div>
  );
};

export default CustomLinksMenu;
